/**
 * Score Report Component
 *
 * This component handles the behavior of the score-report web component
 * that is defined in sections/popup-score-report.liquid.
 *
 * Features:
 * - Accepts data attributes to indicate required fields
 * - Pre-fills first name, last name, and birthdate with logged-in user data
 * - Allows editing of pre-filled fields
 * - Validates date format for birthdate field
 * - Handles form submission (currently just logs to console)
 * - Shows form validation messages
 * - Dynamically adds school rows to both variance and non-variance sections
 * - Initializes date-picker components for all date inputs
 */

class ScoreReportComponent extends HTMLElement {
  constructor() {
    super();

    this.apiBaseUrl = window.theme.internalApp.baseUrl;

    this.form = null;
    this.firstNameInput = null;
    this.lastNameInput = null;
    this.birthDateInput = null;
    this.jdNextIdInput = null;
    this.requiredFields = [];
    this.formErrors = {};
    this.submitButton = null;
    this.errorContainer = null;

    this.isEditMode = false;
    this.editingCartItemData = null;
    this.editingVariantId = null;

    this.userProfile = null;
    this.scoreReportEligibility = null;

    this.entitlementPolicies = {
      entitlementPolicyId: null,
      entitlementPolicyName: null,
    };

    this.withVarianceSchoolCount = 0;
    this.withoutVarianceSchoolCount = 0;

    this._currentStep = this.getAttribute('data-current-step');

    this.isEditMode =
      this.hasAttribute('data-edit-mode') ||
      this.getAttribute('data-edit-mode') === 'true' ||
      window.location.pathname.includes('/cart');

    if (window.scoreReportEditMode === true) {
      window.scoreReportEditMode = undefined;
    }
  }

  async connectedCallback() {
    this.form = this.querySelector('#score-report-form');

    if (!this.form) {
      return;
    }

    this.firstNameInput = this.querySelector('#first-name');
    this.lastNameInput = this.querySelector('#last-name');
    this.birthDateInput = this.querySelector('#birth-date');
    this.jdNextIdInput = this.querySelector('#jd-next-id');
    this.submitButton = this.form.querySelector('.js-score-report-submit');

    this.loaderOverlay = this.querySelector('#score-report-loader-overlay');
    this.orderSummary = this.querySelector('.order-summary');

    this.popupComponent = this.closest('popup-component');
    this.closeBtns = this.querySelectorAll('.jsScoreReportClose');
    this.schoolRowTemplate = this.querySelector('#school-row-template');

    this.triggerElement = null;

    this.closeBtns.forEach(btn => {
      btn.addEventListener('click', e => {
        e.preventDefault();
        e.stopPropagation();

        this.closePopup();
      });
    });

    this.setupConfirmationButtons();

    if (this.orderSummary) {
      this.additionalReportsElement = this.orderSummary.querySelector('.additional-reports');
      this.complimentaryReportsElement = this.orderSummary.querySelector('.complimentary-reports');
      this.subTotalElement = this.orderSummary.querySelector('.subtotal-amount');
    }

    this.showLoader();

    if (this.isEditMode) {
      await this.initializeEditMode();
    } else {
      await this.initializeNewMode();
    }

    if (!this.scoreReportEligibility || !this.scoreReportEligibility.isEligible) {
      this.currentStep = 'not-verified';
    } else {
      this.currentStep = '1';

      this.createErrorContainer();
      this.setupRequiredFields();
      this.setupInputValidation();
      this.setupFormSubmission();
      this.setupAddSchoolButtons();
      this.setupInitialSchoolRows();
      this.setupRealTimeValidation();
      this.setupValidationInfoBoxCloseHandlers();

      this.clearSchoolErrors();
      this.initializeDatePickers();
      this.updateOrderSummary();
      this.updateButtonState();
    }

    this.hideLoader();
    this.dispatchEvent(new Event('score-report:initialized'));
  }

  get currentStep() {
    return this._currentStep;
  }

  set currentStep(step) {
    this._currentStep = step;

    this.setAttribute('data-current-step', step);
  }

  get variantId() {
    return this.dataset.variantId ? parseInt(this.dataset.variantId, 10) : 0;
  }

  get variantPrice() {
    return this.dataset.variantPrice ? parseFloat(this.dataset.variantPrice) : 0;
  }

  /**
   * Format price using the existing price-component logic
   * @param {number} cents - Price in cents
   * @returns {string} Formatted price string
   */
  formatPrice(cents) {
    const currency = window.Shopify?.currency.active || window.theme?.settings?.currency || 'USD';

    const moneyFormat =
      window.currencies_default?.moneyFormats?.[currency]?.money_format ||
      window.theme?.settings?.currencyFormat ||
      '${{amount}}';

    if (window.currencies_default?.formatMoney) {
      return window.currencies_default.formatMoney(cents, moneyFormat);
    }

    return `${window.theme?.settings?.currency_symbol || '$'}${(cents / 100).toFixed(2)}`;
  }

  setupRequiredFields() {
    const requiredFields = this.getAttribute('required-fields');

    if (requiredFields) {
      this.requiredFields = requiredFields.split(',').map(field => field.trim());

      this.requiredFields.forEach(field => {
        const input = this.querySelector(`#${field}`);

        if (input) {
          input.setAttribute('required', 'required');
        }
      });
    }
  }

  async initializeEditMode() {
    await this.fetchScoreReportEligibility();
  }

  async initializeNewMode() {
    await Promise.all([this.fetchUserProfile(), this.fetchScoreReportEligibility(), this.fetchEntitlementPolicies()]);
  }

  async fetchEntitlementPolicies() {
    try {
      const currentVariantId = this.getCurrentVariantId();

      if (!currentVariantId) {
        console.warn('No variant ID found for entitlement policies fetch');

        return;
      }

      const { entitlementPolicyId, entitlementPolicyName } =
        await this.getEntitlementPoliciesForVariant(currentVariantId);

      this.entitlementPolicies = {
        entitlementPolicyId,
        entitlementPolicyName,
      };
    } catch (error) {
      console.error('Error fetching entitlement policies in connectedCallback:', error);
    }
  }

  async fetchScoreReportEligibility() {
    try {
      if (!this.variantId) {
        throw new Error('Variant ID is not set');
      }

      const response = await fetch(`${this.apiBaseUrl}/customers/score-report-eligibility?variantId=${this.variantId}`);

      if (!response.ok) {
        throw new Error('Failed to fetch customer entitlements');
      }

      const data = await response.json();

      this.scoreReportEligibility = data;
    } catch (error) {
      console.error('Error fetching customer entitlements:', error);
    }
  }

  createErrorContainer() {
    if (this.form && !this.errorContainer) {
      this.errorContainer = document.createElement('div');
      this.errorContainer.className = 'form-errors';
      this.errorContainer.style.color = '#d82c0d';
      this.errorContainer.style.marginBottom = '15px';
      this.errorContainer.style.display = 'none';

      if (this.form.firstChild) {
        this.form.insertBefore(this.errorContainer, this.form.firstChild);
      } else {
        this.form.appendChild(this.errorContainer);
      }
    }
  }

  setupInputValidation() {
    if (this.birthDateInput) {
      this.birthDateInput.addEventListener('blur', () => {
        this.validateDateFormat(this.birthDateInput);
      });
    }

    this.requiredFields.forEach(field => {
      const input = this.querySelector(`#${field}`);

      if (input) {
        input.addEventListener('blur', () => {
          this.validateRequiredField(input);
        });
      }
    });
  }

  validateDateFormat(input) {
    if (!input.value) {
      return true;
    }

    if (input.closest('aspen-date-picker') || input.closest('date-picker')) {
      this.clearInputError(input);
      delete this.formErrors[input.id];

      return true;
    }

    const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/(19|20)\d\d$/;
    const isValid = dateRegex.test(input.value);

    if (!isValid) {
      this.formErrors[input.id] = 'Please enter a valid date in MM/DD/YYYY format';
      this.showInputError(input, this.formErrors[input.id]);

      return false;
    } else {
      this.clearInputError(input);
      delete this.formErrors[input.id];

      return true;
    }
  }

  validateRequiredField(input) {
    if (!input.value.trim()) {
      this.formErrors[input.id] = `${this.getLabelText(input)} is required`;
      this.showInputError(input, this.formErrors[input.id]);

      return false;
    } else {
      this.clearInputError(input);
      delete this.formErrors[input.id];

      return true;
    }
  }

  getLabelText(input) {
    const label = this.querySelector(`label[for="${input.id}"]`);

    return label ? label.textContent : input.id;
  }

  showInputError(input, message) {
    input.classList.add('error');
    input.style.borderColor = '#d82c0d';

    const aspenDatePicker = input.closest('aspen-date-picker');
    let container = input.parentNode;

    if (aspenDatePicker) {
      container = aspenDatePicker.parentNode;
    }

    let errorMessage = container.querySelector('.error-message');

    if (!errorMessage) {
      errorMessage = document.createElement('div');
      errorMessage.className = 'error-message';
      errorMessage.style.color = '#d82c0d';
      errorMessage.style.fontSize = '12px';
      errorMessage.style.marginTop = '5px';
      errorMessage.style.clear = 'both';
      errorMessage.style.display = 'block';
      container.appendChild(errorMessage);
    }

    errorMessage.textContent = message;
  }

  clearInputError(input) {
    input.classList.remove('error');
    input.style.borderColor = '';

    const aspenDatePicker = input.closest('aspen-date-picker');
    let container = input.parentNode;

    if (aspenDatePicker) {
      container = aspenDatePicker.parentNode;
    }

    const errorMessage = container.querySelector('.error-message');

    if (errorMessage) {
      errorMessage.remove();
    }
  }

  showFormErrors() {
    if (!this.errorContainer) {
      return;
    }

    if (Object.keys(this.formErrors).length > 0) {
      const errorList = document.createElement('ul');
      errorList.style.margin = '10px 0';
      errorList.style.paddingLeft = '20px';

      Object.values(this.formErrors).forEach(error => {
        const errorItem = document.createElement('li');
        errorItem.textContent = error;
        errorList.appendChild(errorItem);
      });

      this.errorContainer.innerHTML = '<strong>Please correct the following errors:</strong>';
      this.errorContainer.appendChild(errorList);
      this.errorContainer.style.display = 'block';
    } else {
      this.errorContainer.style.display = 'none';
      this.errorContainer.innerHTML = '';
    }
  }

  validateForm() {
    this.formErrors = {};

    this.requiredFields.forEach(field => {
      const input = this.querySelector(`#${field}`);

      if (input) {
        this.validateRequiredField(input);
      }
    });

    if (this.birthDateInput && this.birthDateInput.value) {
      this.validateDateFormat(this.birthDateInput);
    }

    const hasValidSchools = this.validateSchoolSelection();

    this.showFormErrors();

    return Object.keys(this.formErrors).length === 0 && hasValidSchools;
  }

  validateSchoolSelection() {
    const withVarianceRows = this.querySelectorAll('.with-variance-schools .school-row');
    const withoutVarianceRows = this.querySelectorAll('.without-variance-schools .school-row');

    let hasSchoolWithVariance = false;
    let hasSchoolWithoutVariance = false;
    let hasValidationErrors = false;
    let hasMissingDatesForSelectedSchools = false;

    withVarianceRows.forEach(row => {
      const schoolInput = row.querySelector('school-score-handle input');
      const dateInput = row.querySelector('aspen-date-picker input');

      if (this.isValidSchoolSelection(schoolInput)) {
        const hasDate = dateInput && dateInput.value && dateInput.value.trim() !== '';

        if (hasDate) {
          hasSchoolWithVariance = true;
        } else {
          hasValidationErrors = true;
          hasMissingDatesForSelectedSchools = true;
          this.showDateRequiredError(dateInput || row);
        }
      }
    });

    withoutVarianceRows.forEach(row => {
      const schoolInput = row.querySelector('school-score-handle input');
      const dateInput = row.querySelector('aspen-date-picker input');

      if (this.isValidSchoolSelection(schoolInput)) {
        const hasDate = dateInput && dateInput.value && dateInput.value.trim() !== '';

        if (hasDate) {
          hasSchoolWithoutVariance = true;
        } else {
          hasValidationErrors = true;
          hasMissingDatesForSelectedSchools = true;
          this.showDateRequiredError(dateInput || row);
        }
      }
    });

    this.clearSchoolErrors();

    if (hasValidationErrors) {
      // Show specific error based on the situation
      if (hasMissingDatesForSelectedSchools) {
        this.showDateMissingError();
      }

      return false;
    }

    if (!hasSchoolWithVariance && !hasSchoolWithoutVariance) {
      // Show generic error when no schools are selected at all
      this.showSchoolSelectionError();

      return false;
    }

    return true;
  }

  /**
   * Validates if a school input has a valid school selection
   * @param {HTMLInputElement} schoolInput - The school input element
   * @returns {boolean} - True if a valid school is selected
   */
  isValidSchoolSelection(schoolInput) {
    if (!schoolInput || !schoolInput.value.trim()) {
      return false;
    }

    const schoolId = schoolInput.dataset.schoolId;

    return schoolId && schoolId.trim() !== '';
  }

  /**
   * Validates a school input in real time and shows visual feedback
   * @param {HTMLInputElement} schoolInput - The school input element
   */
  validateSchoolInputInRealTime(schoolInput) {
    if (!schoolInput) {
      return;
    }

    const hasText = schoolInput.value && schoolInput.value.trim() !== '';
    const hasValidSelection = this.isValidSchoolSelection(schoolInput);

    schoolInput.style.borderColor = '';
    schoolInput.style.borderWidth = '';

    if (hasText && !hasValidSelection) {
      schoolInput.style.borderColor = '#DC2B2B';
      schoolInput.style.borderWidth = '2px';

      if (!schoolInput.hasAttribute('data-invalid-warning-shown')) {
        schoolInput.setAttribute('data-invalid-warning-shown', 'true');
        schoolInput.title = 'Please select a school from the dropdown menu';
      }
    } else if (hasValidSelection) {
      schoolInput.removeAttribute('data-invalid-warning-shown');
      schoolInput.title = '';

      const schoolRow = schoolInput.closest('.school-row');

      if (schoolRow) {
        const dateInput = schoolRow.querySelector('aspen-date-picker input');

        this.validateDateForSchool(dateInput);
      }
    }
  }

  validateDateForSchool(dateInput) {
    if (!dateInput) {
      return;
    }

    const schoolRow = dateInput.closest('.school-row');

    if (!schoolRow) {
      return;
    }

    const schoolInput = schoolRow.querySelector('school-score-handle input');
    const hasValidSchool = this.isValidSchoolSelection(schoolInput);
    const hasDate = dateInput.value && dateInput.value.trim() !== '';

    this.clearInputError(dateInput);

    if (hasValidSchool && !hasDate) {
      this.showDateRequiredError(dateInput);
    }
  }

  showDateRequiredError(dateInput) {
    if (!dateInput) {
      return;
    }

    const aspenDatePicker = dateInput.closest('aspen-date-picker');
    let container = dateInput.parentNode;

    if (aspenDatePicker) {
      container = aspenDatePicker.parentNode;
    }

    let errorMessage = container.querySelector('.error-message');

    if (!errorMessage) {
      errorMessage = document.createElement('div');
      errorMessage.className = 'error-message';
      errorMessage.style.color = '#d82c0d';
      errorMessage.style.fontSize = '12px';
      errorMessage.style.marginTop = '5px';
      errorMessage.style.clear = 'both';
      errorMessage.style.display = 'block';
      container.appendChild(errorMessage);
    }

    errorMessage.textContent = 'Date is required when a school is selected';

    dateInput.style.borderColor = '#DC2B2B';
    dateInput.style.borderWidth = '2px';
  }

  showSchoolSelectionError() {
    const errorBoxes = this.querySelectorAll('.school-selection-error');

    errorBoxes.forEach(errorBox => {
      if (errorBox) {
        errorBox.classList.remove('hidden');
        errorBox.style.display = 'grid';
        errorBox.style.visibility = 'visible';
        errorBox.style.opacity = '1';

        document.dispatchEvent(
          new CustomEvent('info-box:shown', {
            detail: { element: errorBox },
          })
        );
      }
    });

    if (errorBoxes.length === 0) {
      console.error('School selection error boxes not found!');
    }

    this.addErrorBordersToSchoolInputs();
  }

  showDateMissingError() {
    const errorBoxes = this.querySelectorAll('.school-selection-error');

    errorBoxes.forEach(errorBox => {
      if (errorBox) {
        // Update the message to be more specific about missing dates
        const alertMessage = errorBox.querySelector('.alert-message');

        if (alertMessage) {
          alertMessage.textContent = 'Date is required when a school is selected';
        }

        errorBox.classList.remove('hidden');
        errorBox.style.display = 'grid';
        errorBox.style.visibility = 'visible';
        errorBox.style.opacity = '1';

        document.dispatchEvent(
          new CustomEvent('info-box:shown', {
            detail: { element: errorBox },
          })
        );
      }
    });

    if (errorBoxes.length === 0) {
      console.error('School selection error boxes not found!');
    }

    this.addErrorBordersToSchoolInputs();
  }

  clearSchoolErrors() {
    const errorBoxes = this.querySelectorAll('.school-selection-error');

    errorBoxes.forEach(errorBox => {
      if (errorBox) {
        // Restore the original message from data attribute
        const alertMessage = errorBox.querySelector('.alert-message');
        const originalMessage = errorBox.getAttribute('data-original-message');

        if (alertMessage && originalMessage) {
          alertMessage.textContent = originalMessage;
        } else if (alertMessage) {
          // Fallback to default message if data attribute is not found
          alertMessage.textContent = 'Please enter at least one school name before submitting the form';
        }

        errorBox.classList.add('hidden');
        errorBox.style.display = 'none';
        errorBox.style.visibility = 'hidden';
        errorBox.style.opacity = '0';
      }
    });

    this.removeErrorBordersFromSchoolInputs();
  }

  addErrorBordersToSchoolInputs() {
    const schoolInputs = this.querySelectorAll('school-score-handle input, aspen-date-picker input');

    schoolInputs.forEach(input => {
      const isEmpty = !input.value || input.value.trim() === '';
      const isDatePicker = input.closest('aspen-date-picker');

      if (isDatePicker) {
        const dateValue = input.value;

        if (!dateValue || dateValue.trim() === '') {
          input.style.borderColor = '#DC2B2B';
          input.style.borderWidth = '2px';
        }
      } else {
        const hasText = input.value && input.value.trim() !== '';
        const hasValidSelection = this.isValidSchoolSelection(input);

        if (isEmpty || (hasText && !hasValidSelection)) {
          input.style.borderColor = '#DC2B2B';
          input.style.borderWidth = '2px';
        }
      }
    });
  }

  removeErrorBordersFromSchoolInputs() {
    const schoolInputs = this.querySelectorAll('school-score-handle input, aspen-date-picker input');

    schoolInputs.forEach(input => {
      input.style.borderColor = '';
      input.style.borderWidth = '';
    });
  }

  setupRealTimeValidation() {
    const requiredInputs = this.querySelectorAll('input[required]');

    requiredInputs.forEach(input => {
      input.addEventListener('input', () => {
        this.clearInputError(input);
        this.updateButtonState();
      });

      input.addEventListener('change', () => {
        this.clearInputError(input);
        this.updateButtonState();
      });
    });

    const consentCheckbox = this.querySelector('#data-sharing-consent');

    if (consentCheckbox) {
      consentCheckbox.addEventListener('change', () => {
        if (consentCheckbox.checked) {
          const consentErrorBox = this.querySelector('.consent-error-message');

          if (consentErrorBox) {
            consentErrorBox.classList.add('hidden');
            consentErrorBox.style.display = 'none';
          }

          const consentLabel = this.querySelector('label[for="data-sharing-consent"]');

          if (consentLabel) {
            consentLabel.style.color = '';
          }

          consentCheckbox.style.borderColor = '';
          consentCheckbox.style.borderWidth = '';
          consentCheckbox.style.appearance = 'auto';
        }

        this.updateButtonState();
      });
    }

    this.addEventListener('input', event => {
      const target = event.target;

      if (target.matches('.school-score-handle input, .school-row input[type="text"]')) {
        this.clearSchoolErrors();
        this.validateSchoolInputInRealTime(target);
        this.updateOrderSummary();
        this.updateButtonState();
      }
    });

    this.addEventListener('change', event => {
      const target = event.target;

      if (target.matches('.school-row input[type="text"], .school-row aspen-date-picker input')) {
        if (target.matches('.school-score-handle input')) {
          this.validateSchoolInputInRealTime(target);
        }

        if (target.matches('aspen-date-picker input')) {
          this.validateDateForSchool(target);
        }

        this.updateOrderSummary();
        this.updateButtonState();
      }
    });
  }

  isFormValid() {
    const requiredInputs = this.querySelectorAll('input[required]');

    for (let input of requiredInputs) {
      const value = input.value.trim();

      if (!value) {
        return false;
      }
    }

    const hasSchools = this.hasAtLeastOneSchool();

    if (!hasSchools) {
      return false;
    }

    const consentCheckbox = this.querySelector('#data-sharing-consent');

    if (consentCheckbox && !consentCheckbox.checked) {
      return false;
    }

    return true;
  }

  hasAtLeastOneSchool() {
    const withVarianceRows = this.querySelectorAll('.with-variance-schools .school-row');
    const withoutVarianceRows = this.querySelectorAll('.without-variance-schools .school-row');

    for (let row of withVarianceRows) {
      const schoolInput = row.querySelector('.school-score-handle input, input[type="text"]');
      const dateInput = row.querySelector('aspen-date-picker input');

      if (this.isValidSchoolSelection(schoolInput)) {
        const hasDate = dateInput && dateInput.value && dateInput.value.trim() !== '';

        if (hasDate) {
          return true;
        }
      }
    }

    for (let row of withoutVarianceRows) {
      const schoolInput = row.querySelector('.school-score-handle input, input[type="text"]');
      const dateInput = row.querySelector('aspen-date-picker input');

      if (this.isValidSchoolSelection(schoolInput)) {
        const hasDate = dateInput && dateInput.value && dateInput.value.trim() !== '';

        if (hasDate) {
          return true;
        }
      }
    }

    return false;
  }

  updateButtonState() {
    if (!this.submitButton) {
      return;
    }

    if (this.isFormValid()) {
      this.submitButton.classList.remove('btn-disabled');
    } else {
      this.submitButton.classList.add('btn-disabled');
    }
  }

  showAllErrors() {
    const requiredInputs = this.querySelectorAll('input[required]');
    let hasErrors = false;

    requiredInputs.forEach(input => {
      if (!input.value.trim()) {
        const labelText = this.getLabelText(input);
        this.showInputError(input, `${labelText} is required`);
        hasErrors = true;
      }
    });

    if (!this.hasAtLeastOneSchool()) {
      this.showSchoolSelectionError();
      hasErrors = true;
    }

    const consentCheckbox = this.querySelector('#data-sharing-consent');

    if (consentCheckbox && !consentCheckbox.checked) {
      const consentErrorBox = this.querySelector('.consent-error-message');

      if (consentErrorBox) {
        consentErrorBox.classList.remove('hidden');
        consentErrorBox.style.display = 'grid';
      }

      const consentLabel = this.querySelector('label[for="data-sharing-consent"]');

      if (consentLabel) {
        consentLabel.style.color = '#DC2B2B';
      }

      consentCheckbox.style.borderColor = '#DC2B2B';
      consentCheckbox.style.borderWidth = '2px';
      consentCheckbox.style.appearance = 'none';

      hasErrors = true;
    }

    if (hasErrors) {
      const popup = this.closest('popup-component');

      if (popup) {
        const popupContent = popup.querySelector('.popup-content, .popup-wrapper, .popup');

        if (popupContent) {
          popupContent.scrollTop = 0;
        }
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }

    return hasErrors;
  }

  setupFormSubmission() {
    if (!this.form || !this.submitButton) {
      return;
    }

    this.submitButton.addEventListener('click', async event => {
      const isButtonDisabled = this.submitButton.classList.contains('btn-disabled');

      event.preventDefault();
      event.stopPropagation();

      if (isButtonDisabled || !this.validateForm()) {
        this.showAllErrors();

        return;
      }

      const scoreReportData = await this.collectFormData();

      if (this.isEditMode) {
        await this.updateCartItem(scoreReportData);
      } else {
        await this.createCartItem(scoreReportData);
      }
    });
  }

  async createCartItem(scoreReportData) {
    const submitButton = this.querySelector('.js-score-report-submit');
    const originalButtonText = submitButton ? submitButton.textContent : '';

    this.showLoader();

    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = 'Adding to Cart...';
    }

    try {
      await this.addScoreReportPropertiesToForm(scoreReportData);

      let productForm = this.closest('product-form');

      if (!productForm) {
        productForm = document.querySelector('product-form.score-report-product-form');
      }

      if (!productForm) {
        productForm = document.querySelector('product-form');
      }

      if (!productForm) {
        return;
      }

      let productCartForm = productForm.querySelector('product-cart-form');

      if (!productCartForm) {
        return;
      }

      this.setVariantIdFromPage();

      productCartForm.dataset.scoreReportCompleted = 'true';

      const formElement = productCartForm.querySelector('form#score-report-product-form');

      if (!formElement) {
        return;
      }

      const formData = new FormData(formElement);

      let formDataEntries = [];

      for (let [key, value] of formData.entries()) {
        formDataEntries.push({ key, value });
      }

      formData.append('sections', productCartForm.sectionList());
      formData.append('sections_url', productCartForm.dataset.url || location.pathname);

      await productCartForm.cartAdd(formData);
      this.closeScoreReportPopup(true);
    } catch (error) {
      this.hideLoader();

      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = originalButtonText;
      }

      throw error;
    } finally {
      this.hideLoader();

      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = originalButtonText;
      }
    }
  }

  async updateCartItem(scoreReportData) {
    try {
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();

      const cartItem = cartData.items.find(item => item.variant_id.toString() === this.editingVariantId.toString());

      if (!cartItem) {
        console.error('❌ Cart item not found!');

        return;
      }

      const newProperties = {};

      if (cartItem.properties) {
        Object.keys(cartItem.properties).forEach(key => {
          if (!key.startsWith('_score_report_') && !key.startsWith('_school_')) {
            newProperties[key] = cartItem.properties[key];
          }
        });
      }

      newProperties._score_report_first_name = scoreReportData.firstName;
      newProperties._score_report_last_name = scoreReportData.lastName;
      newProperties._score_report_birth_date = scoreReportData.birthDate;
      newProperties._score_report_jd_next_id = scoreReportData.jdNextId;

      const quantity = scoreReportData.schoolsWithVariance.length + scoreReportData.schoolsWithoutVariance.length;

      scoreReportData.schoolsWithVariance.forEach((school, index) => {
        const num = index + 1;

        newProperties[`_school_with_${num}`] = school.name;
        newProperties[`_school_with_id_${num}`] = school.id;
        newProperties[`_school_with_date_${num}`] = school.dateApplied;
      });

      scoreReportData.schoolsWithoutVariance.forEach((school, index) => {
        const num = index + 1;

        newProperties[`_school_without_${num}`] = school.name;
        newProperties[`_school_without_id_${num}`] = school.id;
        newProperties[`_school_without_date_${num}`] = school.dateApplied;
      });

      const changeData = {
        id: cartItem.key,
        quantity: quantity,
        properties: newProperties,
      };

      const changeResponse = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(changeData),
      });

      if (!changeResponse.ok) {
        const errorText = await changeResponse.text();
        console.error('❌ Failed to update cart item:', changeResponse.status, errorText);
        throw new Error(`Failed to update cart item: ${changeResponse.status}`);
      }

      this.closeScoreReportPopup(true);

      if (window.location.pathname.includes('/cart')) {
        window.location.reload();
      }
    } catch (error) {
      console.error('❌ DEBUGGING: Error updating cart item:', error);
    }
  }

  async collectFormData() {
    const formData = {
      firstName: this.firstNameInput?.value || '',
      lastName: this.lastNameInput?.value || '',
      birthDate: this.birthDateInput?.value || '',
      jdNextId: this.jdNextIdInput?.value || '',
      schoolsWithVariance: [],
      schoolsWithoutVariance: [],
    };

    const withVarianceRows = this.querySelectorAll('.with-variance-schools .school-row');

    withVarianceRows.forEach((row, index) => {
      const schoolInput = row.querySelector('school-score-handle input');
      const dateInput = row.querySelector('aspen-date-picker input');

      if (schoolInput && schoolInput.value.trim()) {
        let dateApplied = null;

        if (dateInput && dateInput.value) {
          dateApplied = dateInput.value;
        } else if (this.isEditMode && this.editingCartItemData) {
          const originalSchool = this.editingCartItemData.schoolsWithVariance?.[index];

          if (originalSchool && originalSchool.dateApplied) {
            dateApplied = originalSchool.dateApplied;
          }
        }

        if (dateApplied) {
          let schoolId = schoolInput.dataset.schoolId;

          if (!schoolId && this.isEditMode && this.editingCartItemData) {
            const originalSchool = this.editingCartItemData.schoolsWithVariance?.[index];

            if (originalSchool && originalSchool.id) {
              schoolId = originalSchool.id;
            }
          }

          if (!schoolId) {
            schoolId = `dummy-with-${index + 1}`;
          }

          formData.schoolsWithVariance.push({
            id: schoolId,
            name: schoolInput.value.trim(),
            dateApplied: dateApplied,
          });
        }
      }
    });

    const withoutVarianceRows = this.querySelectorAll('.without-variance-schools .school-row');

    withoutVarianceRows.forEach((row, index) => {
      const schoolInput = row.querySelector('school-score-handle input');
      const dateInput = row.querySelector('aspen-date-picker input');

      if (schoolInput && schoolInput.value.trim()) {
        let dateApplied = null;

        if (dateInput && dateInput.value) {
          dateApplied = dateInput.value;
        } else if (this.isEditMode && this.editingCartItemData) {
          const originalSchool = this.editingCartItemData.schoolsWithoutVariance?.[index];

          if (originalSchool && originalSchool.dateApplied) {
            dateApplied = originalSchool.dateApplied;
          }
        }

        if (dateApplied) {
          let schoolId = schoolInput.dataset.schoolId;

          if (!schoolId && this.isEditMode && this.editingCartItemData) {
            const originalSchool = this.editingCartItemData.schoolsWithoutVariance?.[index];

            if (originalSchool && originalSchool.id) {
              schoolId = originalSchool.id;
            }
          }

          if (!schoolId) {
            schoolId = `dummy-without-${index + 1}`;
          }

          formData.schoolsWithoutVariance.push({
            id: schoolId,
            name: schoolInput.value.trim(),
            dateApplied: dateApplied,
          });
        }
      }
    });

    return formData;
  }

  async updateOrderSummary() {
    if (!this.orderSummary) {
      return;
    }

    const totalSchools = this.getValidSchoolsCount();

    let additionalReportsTotal = 0;
    let complimentaryReportsTotal = 0;

    if (this.scoreReportEligibility.lifetimeFree) {
      complimentaryReportsTotal = totalSchools;
    } else {
      complimentaryReportsTotal = Math.min(this.scoreReportEligibility.freeScoreReports, totalSchools);
      additionalReportsTotal = Math.max(0, totalSchools - this.scoreReportEligibility.freeScoreReports);
    }

    this.complimentaryReportsElement.textContent = `${complimentaryReportsTotal} x ${this.formatPrice(0)}`;
    this.additionalReportsElement.textContent = `${additionalReportsTotal} x ${this.formatPrice(this.variantPrice)}`;

    if (this.subTotalElement) {
      this.subTotalElement.textContent = `${this.formatPrice(additionalReportsTotal * this.variantPrice)}`;
    }
  }

  async getEntitlementPoliciesForVariant(variantId) {
    try {
      if (!variantId) {
        throw new Error('Variant ID is required');
      }

      const entitlementResponse = await fetch(`${this.apiBaseUrl}/entitlement-policies?variantId=${variantId}`);

      if (!entitlementResponse.ok) {
        throw new Error(`Error getting entitlement policies: ${entitlementResponse.status}`);
      }

      const entitlementsData = await entitlementResponse.json();

      if (!Array.isArray(entitlementsData) || entitlementsData.length === 0) {
        return { entitlementPolicyId: null, entitlementPolicyName: null };
      }

      const variantEntitlements = entitlementsData.find(item => item.variantId.toString() === variantId.toString());

      if (
        !variantEntitlements ||
        !variantEntitlements.variantEntitlementPolicies ||
        variantEntitlements.variantEntitlementPolicies.length === 0
      ) {
        return { entitlementPolicyId: null, entitlementPolicyName: null };
      }

      const firstEntitlement = variantEntitlements.variantEntitlementPolicies[0];

      return {
        entitlementPolicyId: firstEntitlement.entitlementPolicyV2.id,
        entitlementPolicyName: firstEntitlement.entitlementPolicyV2.title,
      };
    } catch (error) {
      console.error('Error fetching entitlement policies:', error);

      return { entitlementPolicyId: null, entitlementPolicyName: null };
    }
  }

  async addScoreReportPropertiesToForm(scoreReportData) {
    let productForm = this.closest('product-form');

    if (!productForm) {
      productForm = document.querySelector('product-form.score-report-product-form');
    }

    if (!productForm) {
      console.error('product-form not found');

      return;
    }

    const productCartForm = productForm.querySelector('product-cart-form');

    if (!productCartForm) {
      console.error('product-cart-form not found within product-form');

      return;
    }

    const formElement = productCartForm.querySelector('form#score-report-product-form');

    if (!formElement) {
      console.error('HTML form element not found within product-cart-form');

      return;
    }

    const existingProperties = formElement.querySelectorAll('input[name^="properties["]');

    existingProperties.forEach(input => {
      if (
        input.name.includes('_score_report_') ||
        input.name.includes('_school_') ||
        input.name.includes('_entitlementPolicy')
      ) {
        input.remove();
      }
    });

    this.addHiddenInput(formElement, 'properties[_score_report_first_name]', scoreReportData.firstName);
    this.addHiddenInput(formElement, 'properties[_score_report_last_name]', scoreReportData.lastName);
    this.addHiddenInput(formElement, 'properties[_score_report_birth_date]', scoreReportData.birthDate);
    this.addHiddenInput(formElement, 'properties[_score_report_jd_next_id]', scoreReportData.jdNextId);

    const quantity = scoreReportData.schoolsWithVariance.length + scoreReportData.schoolsWithoutVariance.length;
    const quantityInput = formElement.querySelector('input[name="quantity"]');

    if (quantityInput) {
      quantityInput.value = quantity.toString();
    } else {
      this.addHiddenInput(formElement, 'quantity', quantity.toString());
    }

    if (this.entitlementPolicies.entitlementPolicyId && this.entitlementPolicies.entitlementPolicyName) {
      this.addHiddenInput(
        formElement,
        'properties[_entitlementPolicyId]',
        this.entitlementPolicies.entitlementPolicyId
      );

      this.addHiddenInput(
        formElement,
        'properties[_entitlementPolicyName]',
        this.entitlementPolicies.entitlementPolicyName
      );
    }

    scoreReportData.schoolsWithVariance.forEach((school, index) => {
      const num = index + 1;

      this.addHiddenInput(formElement, `properties[_school_with_${num}]`, school.name);
      this.addHiddenInput(formElement, `properties[_school_with_id_${num}]`, school.id);
      this.addHiddenInput(formElement, `properties[_school_with_date_${num}]`, school.dateApplied);
    });

    scoreReportData.schoolsWithoutVariance.forEach((school, index) => {
      const num = index + 1;

      this.addHiddenInput(formElement, `properties[_school_without_${num}]`, school.name);
      this.addHiddenInput(formElement, `properties[_school_without_id_${num}]`, school.id);
      this.addHiddenInput(formElement, `properties[_school_without_date_${num}]`, school.dateApplied);
    });
  }

  addHiddenInput(form, name, value) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }

  closeScoreReportPopup() {
    if (this.popupComponent && typeof this.popupComponent.close === 'function') {
      this.popupComponent.close();
    }

    document.dispatchEvent(new CustomEvent('score-report:submitted', { bubbles: true }));
  }

  setVariantIdFromPage() {
    let productForm = this.closest('product-form');

    if (!productForm) {
      productForm = document.querySelector('product-form.score-report-product-form');
    }

    if (!productForm) {
      return;
    }

    const productCartForm = productForm.querySelector('product-cart-form');

    if (!productCartForm) {
      return;
    }

    const variantInput = productCartForm.querySelector('input[name="id"]');

    if (!variantInput) {
      return;
    }

    let variantId = null;

    const isInQuickShop = document.querySelector('.quickshop-popup') || document.querySelector('quickshop-component');

    if (isInQuickShop) {
      const quickShopComponent = document.querySelector('quickshop-component');

      if (quickShopComponent) {
        const activeQuickShopVariant = quickShopComponent.querySelector('.quickshop-variant-item.active');

        if (activeQuickShopVariant && activeQuickShopVariant.dataset.variantId) {
          variantId = activeQuickShopVariant.dataset.variantId;
        }

        if (!variantId) {
          const quickShopInput = quickShopComponent.querySelector('input[name="id"], select[name="id"]');

          if (quickShopInput && quickShopInput.value) {
            variantId = quickShopInput.value;
          }
        }
      }
    }

    if (!variantId) {
      const selectedVariant = document.querySelector('input[name="id"]:checked');

      if (selectedVariant) {
        variantId = selectedVariant.value;
      }
    }

    if (!variantId) {
      const productFormInput = document.querySelector('product-form input[name="id"]');

      if (productFormInput && productFormInput.value) {
        variantId = productFormInput.value;
      }
    }

    if (!variantId) {
      const urlParams = new URLSearchParams(window.location.search);
      variantId = urlParams.get('variant');
    }

    if (!variantId) {
      const variantElement = document.querySelector('[data-variant-id]');

      if (variantElement && variantElement.dataset.variantId) {
        variantId = variantElement.dataset.variantId;
      }
    }

    if (variantId) {
      variantInput.value = variantId;
    }
  }

  initializeDatePickers() {
    const datePickers = this.querySelectorAll('aspen-date-picker');

    datePickers.forEach(datePicker => {
      if (typeof datePicker.init === 'function') {
        try {
          datePicker.init();
        } catch (error) {
          console.error(`❌ Error calling init():`, error);
        }
      } else {
        try {
          if (typeof window.AspenDatePicker === 'function') {
            const aspenInstance = new window.AspenDatePicker();

            if (typeof aspenInstance.init === 'function') {
              aspenInstance.init.call(datePicker);
            }
          }
        } catch (error) {
          console.error(`❌ Manual initialization failed:`, error);
        }
      }
    });
  }

  reinitializeDatePickersForEditMode() {
    const datePickers = this.querySelectorAll('aspen-date-picker');

    datePickers.forEach(datePicker => {
      const input = datePicker.querySelector('input');

      if (typeof datePicker.init === 'function') {
        try {
          datePicker.init();

          if (input && input.value && typeof datePicker.parseInputValue === 'function') {
            datePicker.parseInputValue();
          }
        } catch (error) {
          console.error(`❌ Error triggering init():`, error);
        }
      }
    });
  }

  setupAddSchoolButtons() {
    const addSchoolWithVarianceBtn = this.querySelector('.add-school-with-variance');

    if (addSchoolWithVarianceBtn) {
      addSchoolWithVarianceBtn.addEventListener('click', () => {
        this.addSchool('with-variance');
        this.updateOrderSummary();
      });
    }

    const addSchoolWithoutVarianceBtn = this.querySelector('.add-school-without-variance');

    if (addSchoolWithoutVarianceBtn) {
      addSchoolWithoutVarianceBtn.addEventListener('click', () => {
        this.addSchool('without-variance');
        this.updateOrderSummary();
      });
    }

    this.addEventListener('input', event => {
      const schoolInput = event.target.closest('school-score-handle');

      if (schoolInput && event.target.tagName === 'INPUT') {
        this.updateOrderSummary();
      }
    });

    this.addEventListener(
      'blur',
      event => {
        const schoolInput = event.target.closest('school-score-handle');

        if (schoolInput && event.target.tagName === 'INPUT') {
          this.validateSchoolSelection();
        }
      },
      true
    );

    this.addEventListener('click', event => {
      if (event.target.matches('.remove-school-btn, .remove-school')) {
        this.updateOrderSummary();
      }
    });
  }

  setupInitialSchoolRows() {
    this.withVarianceSchoolCount = 0;
    this.withoutVarianceSchoolCount = 0;

    if (this.isEditMode) {
      return;
    } else {
      for (let i = 0; i < 4; i++) {
        this.addSchool('with-variance');
        this.addSchool('without-variance');
      }
    }
  }

  /**
   * Counts only schools that have both a valid school selection and a date
   * This ensures order summary calculations match actual submission logic
   * @returns {number} Count of valid schools that would be submitted
   */
  getValidSchoolsCount() {
    const withVarianceRows = this.querySelectorAll('.with-variance-schools .school-row');
    const withoutVarianceRows = this.querySelectorAll('.without-variance-schools .school-row');

    let validSchoolsCount = 0;

    withVarianceRows.forEach(row => {
      const schoolInput = row.querySelector('school-score-handle input');
      const dateInput = row.querySelector('aspen-date-picker input');

      if (this.isValidSchoolSelection(schoolInput)) {
        const hasDate = dateInput && dateInput.value && dateInput.value.trim() !== '';

        if (hasDate) {
          validSchoolsCount++;
        }
      }
    });

    withoutVarianceRows.forEach(row => {
      const schoolInput = row.querySelector('school-score-handle input');
      const dateInput = row.querySelector('aspen-date-picker input');

      if (this.isValidSchoolSelection(schoolInput)) {
        const hasDate = dateInput && dateInput.value && dateInput.value.trim() !== '';

        if (hasDate) {
          validSchoolsCount++;
        }
      }
    });

    return validSchoolsCount;
  }

  /**
   * Generic method to populate basic user information fields
   * @param {Object} userData - Object containing firstName, lastName, birthDate, jdNextId
   */
  populateBasicUserInfo(userData) {
    if (!userData) {
      return;
    }

    if (this.firstNameInput && userData.firstName !== undefined) {
      this.firstNameInput.value = userData.firstName;
    }

    if (this.lastNameInput && userData.lastName !== undefined) {
      this.lastNameInput.value = userData.lastName;
    }

    if (this.birthDateInput && userData.birthDate !== undefined) {
      this.birthDateInput.value = userData.birthDate;
    }

    if (this.jdNextIdInput && userData.jdNextId !== undefined) {
      this.jdNextIdInput.value = userData.jdNextId;
    }
  }

  async fetchUserProfile() {
    try {
      if (!this.variantId) {
        return;
      }

      const response = await fetch(`${this.apiBaseUrl}/customers/profile`);

      if (!response.ok) {
        throw new Error('Failed to fetch user profile');
      }

      const userProfile = await response.json();

      this.userProfile = userProfile;

      if (!this.isEditMode) {
        const userData = {
          firstName: userProfile.firstName,
          lastName: userProfile.lastName,
          birthDate: userProfile?.userProfileMetadatas?.DateOfBirth
            ? this.formatDateForInput(userProfile.userProfileMetadatas.DateOfBirth)
            : undefined,
          jdNextId: userProfile?.userProfileMetadatas?.['JD-Next ID'],
        };

        this.populateBasicUserInfo(userData);

        if (this.birthDateInput && userProfile?.userProfileMetadatas?.DateOfBirth) {
          this.updateDatePicker(this.birthDateInput, userProfile.userProfileMetadatas.DateOfBirth);
        }
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);

      this.userProfile = null;
    }
  }

  formatDateForInput(isoDate) {
    try {
      if (!isoDate) {
        return '';
      }

      const dateParts = isoDate.split('-');

      if (dateParts.length === 3) {
        const year = parseInt(dateParts[0], 10);
        const month = parseInt(dateParts[1], 10) - 1;
        const day = parseInt(dateParts[2], 10);
        const date = new Date(year, month, day);

        const formattedMonth = String(date.getMonth() + 1).padStart(2, '0');
        const formattedDay = String(date.getDate()).padStart(2, '0');
        const formattedYear = date.getFullYear();

        return `${formattedMonth}/${formattedDay}/${formattedYear}`;
      }

      const date = new Date(isoDate);
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();

      return `${month}/${day}/${year}`;
    } catch (error) {
      console.error('📋 Error formatting date:', error);

      return isoDate;
    }
  }

  updateDatePicker(inputElement, isoDate) {
    try {
      if (!inputElement || !isoDate) {
        return;
      }

      const datePicker = inputElement.closest('aspen-date-picker') || inputElement.closest('date-picker');

      if (datePicker && datePicker.flatpickrInstance) {
        const dateParts = isoDate.split('-');

        if (dateParts.length === 3) {
          const year = parseInt(dateParts[0], 10);
          const month = parseInt(dateParts[1], 10) - 1;
          const day = parseInt(dateParts[2], 10);
          const date = new Date(year, month, day);

          datePicker.flatpickrInstance.setDate(date, false);
        } else {
          const date = new Date(isoDate);
          datePicker.flatpickrInstance.setDate(date, false);
        }
      }
    } catch (error) {
      console.error('📋 Error updating date picker:', error);
    }
  }

  addSchool(sectionType) {
    let container, schoolCount, prefix;

    if (sectionType === 'with-variance') {
      container = this.querySelector('.with-variance-schools');
      schoolCount = ++this.withVarianceSchoolCount;
      prefix = 'with-';
    } else {
      container = this.querySelector('.without-variance-schools');
      schoolCount = ++this.withoutVarianceSchoolCount;
      prefix = 'without-';
    }

    if (!container) {
      return;
    }

    const fragment = this.schoolRowTemplate.content.cloneNode(true);
    const newRow = fragment.querySelector('.school-row');

    if (!newRow) {
      console.error('No .school-row found in template!');

      return;
    }

    this.updateElementIds(newRow, prefix, schoolCount);
    this.addRemoveButtonToRow(newRow, sectionType);

    container.appendChild(newRow);

    this.updateOrderSummary();
  }

  updateElementIds(row, prefix, count, clearValues = true) {
    const dateLabel = row.querySelector('.form-group:nth-child(2) label');
    const dateInput = row.querySelector('aspen-date-picker input');
    const schoolLabel = row.querySelector('.form-group:first-child label');
    const schoolInput = row.querySelector('school-score-handle input');

    if (schoolLabel) {
      schoolLabel.setAttribute('for', `${prefix}school-${count}`);
      schoolLabel.textContent = `School ${count}`;
    }

    if (schoolInput) {
      schoolInput.id = `${prefix}school-${count}`;

      if (clearValues) {
        schoolInput.value = '';
      }
    }

    if (dateLabel) {
      dateLabel.setAttribute('for', `${prefix}date-applied-${count}`);
    }

    if (dateInput) {
      dateInput.id = `${prefix}date-applied-${count}`;

      if (clearValues) {
        dateInput.value = '';
      }
    }
  }

  addRemoveButtonToRow(row, sectionType) {
    if (row.querySelector('.remove-school-btn')) {
      return;
    }

    const removeButton = document.createElement('button');
    removeButton.type = 'submit';
    removeButton.className = 'btn btn-outline-danger remove-school-btn popup-safe-button';
    removeButton.innerHTML = 'X';
    removeButton.title = 'Remove this school';

    removeButton.addEventListener('click', event => {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (event.target) {
        event.target.setAttribute('data-prevent-popup-close', 'true');
      }

      this.removeSchoolRow(row, sectionType);
    });

    const removeButtonContainer = document.createElement('div');
    removeButtonContainer.className = 'remove-school-container';
    removeButtonContainer.appendChild(removeButton);

    row.appendChild(removeButtonContainer);
  }

  removeSchoolRow(row, sectionType) {
    const allSchools = this.querySelectorAll('.school-row');

    if (allSchools.length <= 1) {
      return;
    }

    row.remove();

    if (sectionType === 'with-variance') {
      this.withVarianceSchoolCount--;
    } else {
      this.withoutVarianceSchoolCount--;
    }

    this.updateSchoolRowNumbers(sectionType);
    this.updateOrderSummary();
  }

  updateSchoolRowNumbers(sectionType) {
    const prefix = sectionType === 'with-variance' ? 'with-' : 'without-';

    const container =
      sectionType === 'with-variance'
        ? this.querySelector('.with-variance-schools')
        : this.querySelector('.without-variance-schools');

    if (!container) {
      return;
    }

    const rows = container.querySelectorAll('.school-row');

    rows.forEach((row, index) => {
      this.updateElementIds(row, prefix, index + 1, false);
    });
  }

  getCurrentVariantId() {
    let variantId = null;
    const selectedVariant = document.querySelector('input[name="id"]:checked');

    if (selectedVariant) {
      variantId = selectedVariant.value;
    }

    if (!variantId) {
      const productFormInput = document.querySelector('product-form input[name="id"]');

      if (productFormInput) {
        variantId = productFormInput.value;
      }
    }

    if (!variantId) {
      const urlParams = new URLSearchParams(window.location.search);
      variantId = urlParams.get('variant');
    }

    if (!variantId) {
      const variantElement = document.querySelector('[data-variant-id]');

      if (variantElement) {
        variantId = variantElement.dataset.variantId;
      }
    }

    return variantId;
  }

  updateSubmitButtonForEditMode() {
    if (this.submitButton) {
      this.submitButton.textContent = 'Update Score Report';
    } else {
      const manualButton =
        this.querySelector('button[type="submit"]') ||
        this.querySelector('.btn-submit') ||
        this.querySelector('[data-submit]');

      if (manualButton) {
        manualButton.textContent = 'Update Score Report';
      }
    }
  }

  populateFromSavedData(cartItemData) {
    try {
      if (!cartItemData) {
        console.error('❌ No cart item data provided');

        return;
      }

      if (!this.isEditMode) {
        this.isEditMode = true;
      }

      this.editingCartItemData = cartItemData;
      this.editingVariantId = cartItemData.variant_id;

      const properties = cartItemData.properties || {};

      const userData = {
        firstName: properties._score_report_first_name || '',
        lastName: properties._score_report_last_name || '',
        birthDate: properties._score_report_birth_date || '',
        jdNextId: properties._score_report_jd_next_id || '',
      };

      this.populateBasicUserInfo(userData);

      const schoolsWithVariance = [];
      let index = 1;

      while (properties[`_school_with_${index}`]) {
        schoolsWithVariance.push({
          name: properties[`_school_with_${index}`] || '',
          id: properties[`_school_with_id_${index}`] || '',
          dateApplied: properties[`_school_with_date_${index}`] || '',
        });

        index++;
      }

      const schoolsWithoutVariance = [];

      index = 1;

      while (properties[`_school_without_${index}`]) {
        schoolsWithoutVariance.push({
          name: properties[`_school_without_${index}`] || '',
          id: properties[`_school_without_id_${index}`] || '',
          dateApplied: properties[`_school_without_date_${index}`] || '',
        });

        index++;
      }

      if (schoolsWithVariance.length > 0) {
        this.populateSchoolSection('with', schoolsWithVariance);
      }

      if (schoolsWithoutVariance.length > 0) {
        this.populateSchoolSection('without', schoolsWithoutVariance);
      }

      this.updateSubmitButtonForEditMode();

      setTimeout(() => {
        this.reinitializeDatePickersForEditMode();
      }, 150);
    } catch (error) {
      console.error('❌ Error populating form with saved data:', error);
    }
  }

  populateSchoolSection(sectionType, schools) {
    try {
      const sectionSelector = sectionType === 'with' ? '.with-variance-schools' : '.without-variance-schools';
      const section = this.querySelector(sectionSelector);

      if (!section) {
        return;
      }

      const existingRows = section.querySelectorAll('.school-row');
      existingRows.forEach(row => row.remove());

      if (sectionType === 'with') {
        this.withVarianceSchoolCount = 0;
      } else {
        this.withoutVarianceSchoolCount = 0;
      }

      schools.forEach((school, index) => {
        const addSchoolType = sectionType === 'with' ? 'with-variance' : 'without-variance';
        this.addSchool(addSchoolType);

        setTimeout(() => {
          this.populateSchoolRow(section, school, index + 1, sectionType);
        }, 50);
      });
    } catch (error) {
      console.error(`❌ Error populating ${sectionType} variance school section:`, error);
    }
  }

  populateSchoolRow(section, school, rowNumber, sectionType) {
    try {
      const schoolRows = section.querySelectorAll('.school-row');
      const targetRow = schoolRows[rowNumber - 1];

      if (!targetRow) {
        console.warn(`⚠️ School row ${rowNumber} not found in ${sectionType} variance section`);

        return;
      }

      const schoolInput = targetRow.querySelector('school-score-handle input');

      if (schoolInput && school.name) {
        schoolInput.value = school.name;

        if (school.id) {
          schoolInput.dataset.schoolId = school.id;
        }

        schoolInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      const dateInput = targetRow.querySelector('aspen-date-picker input');

      if (dateInput && school.dateApplied) {
        dateInput.value = school.dateApplied;
        const aspenDatePicker = dateInput.closest('aspen-date-picker');

        if (aspenDatePicker) {
          try {
            if (aspenDatePicker.flatpickrInstance) {
              aspenDatePicker.flatpickrInstance.setDate(school.dateApplied, false);
            } else if (typeof aspenDatePicker.parseInputValue === 'function') {
              aspenDatePicker.parseInputValue();
            }
          } catch (error) {
            console.warn(`⚠️ Could not update date picker instance for school ${rowNumber}:`, error);
          }
        }

        dateInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (this.isEditMode && rowNumber > 1) {
        const addSchoolType = sectionType === 'with' ? 'with-variance' : 'without-variance';
        this.addRemoveButtonToRow(targetRow, addSchoolType);
      }
    } catch (error) {
      console.error(`❌ Error populating school row ${rowNumber}:`, error);
    }
  }

  showLoader() {
    this.classList.add('loading');

    if (this.loaderOverlay) {
      this.loaderOverlay.style.display = 'flex';
    }
  }

  hideLoader() {
    this.classList.remove('loading');

    if (this.loaderOverlay) {
      this.loaderOverlay.style.display = 'none';
    }
  }

  setupValidationInfoBoxCloseHandlers() {
    const validationInfoBoxes = this.querySelectorAll('.school-selection-error, .consent-error-message');

    validationInfoBoxes.forEach(infoBox => {
      const closeButton = infoBox.querySelector('.close-info-box');

      if (closeButton && !closeButton.hasAttribute('data-validation-listener-added')) {
        closeButton.removeEventListener('click', this.handleInfoBoxClose);

        closeButton.addEventListener('click', event => {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();

          infoBox.classList.add('hidden');
          infoBox.style.display = 'none';

          if (infoBox.classList.contains('consent-error-message')) {
            const consentCheckbox = this.querySelector('#data-sharing-consent');
            const consentLabel = this.querySelector('label[for="data-sharing-consent"]');

            if (consentCheckbox) {
              consentCheckbox.style.borderColor = '';
              consentCheckbox.style.borderWidth = '';
              consentCheckbox.style.appearance = 'auto';
            }

            if (consentLabel) {
              consentLabel.style.color = '';
            }
          }

          if (infoBox.classList.contains('school-selection-error')) {
            this.removeErrorBordersFromSchoolInputs();
          }
        });

        closeButton.setAttribute('data-validation-listener-added', 'true');
      }
    });
  }

  hasUserEnteredData() {
    const requiredInputs = this.querySelectorAll('input[required]');

    for (const input of requiredInputs) {
      if (input.value.trim()) {
        return true;
      }
    }

    const schoolInputs = this.querySelectorAll('school-score-handle input');

    for (const input of schoolInputs) {
      if (input.value.trim()) {
        return true;
      }
    }

    const dateInputs = this.querySelectorAll('aspen-date-picker input');

    for (const input of dateInputs) {
      if (input.value.trim()) {
        return true;
      }
    }

    const consentCheckbox = this.querySelector('#data-sharing-consent');

    if (consentCheckbox && consentCheckbox.checked) {
      return true;
    }

    return false;
  }

  closePopup() {
    if (this.currentStep === '1') {
      if (this.isEditMode) {
        this.currentStep = 'confirmation-edit';
      } else {
        this.currentStep = 'confirmation';
      }

      return;
    } else if (this.currentStep === 'confirmation' || this.currentStep === 'confirmation-edit') {
      this.currentStep = '1';

      return;
    }

    if (this.popupComponent && typeof this.popupComponent.close === 'function') {
      this.popupComponent.close();
    }
  }

  setupConfirmationButtons() {
    this.addEventListener('click', event => {
      const button = event.target.closest('.jsConfirmExit, .jsCancelExit');

      if (!button) {
        return;
      }

      const buttonStepElement = button.closest('[data-step]');

      if (!buttonStepElement || buttonStepElement.getAttribute('data-step') !== this.currentStep) {
        return;
      }

      event.preventDefault();

      const isConfirmExit = button.classList.contains('jsConfirmExit');
      const isCancelExit = button.classList.contains('jsCancelExit');

      if (this.currentStep === 'confirmation') {
        if (isConfirmExit) {
          this.handleConfirmExit(button);
        } else if (isCancelExit) {
          this.handleCancelExit();
        }
      } else if (this.currentStep === 'confirmation-edit') {
        if (isConfirmExit) {
          this.handleConfirmExit(button);
        } else if (isCancelExit) {
          this.handleCancelExit();
        }
      }
    });
  }

  handleConfirmExit(button) {
    const elementToFocus =
      this.triggerElement ||
      document.querySelector('cart-edit-score-report') ||
      document.querySelector('[data-popup="popup-score-report"]') ||
      document.querySelector('body');

    if (elementToFocus && elementToFocus.focus) {
      elementToFocus.focus();
    } else {
      button.blur();
    }

    setTimeout(() => {
      this.popupComponent.close();
    }, 10);
  }

  handleCancelExit() {
    this.currentStep = '1';
  }
}

customElements.define('score-report', ScoreReportComponent);
